/**
 * A stand-in for the `admin` function on previews and in local development (no AWS), following
 * contracts/admin-function.md so the people screen (T067) can be tried on a phone. It keeps its
 * people in memory, so they reset on reload like the rest of the preview data. It ignores the
 * access token: there is nothing real behind it to protect.
 */
import type { AdminPerson, AdminTransport } from './client';

/** The fewer-than-5 rule (FR-025): the owner plus at most 4 other people. */
const MAX_MEMBERS = 4;
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export interface FakeAdminOptions {
  ownerEmail: string;
  /** People already approved besides the owner (e.g. the sample member on previews). */
  members: string[];
  /** The id the fake sign-in gives an email, so an added person's cellar id matches. */
  idFor: (email: string) => string;
}

export interface FakeAdminDirectory {
  transport: AdminTransport;
  /** False for a person the owner stopped, so the fake sign-in can refuse them like Cognito would. */
  canSignIn(email: string): boolean;
}

export function createFakeAdminDirectory(options: FakeAdminOptions): FakeAdminDirectory {
  const normalise = (email: string) => email.trim().toLowerCase();
  const person = (email: string, role: AdminPerson['role']): AdminPerson => ({
    id: options.idFor(normalise(email)),
    email: normalise(email),
    role,
    enabled: true,
  });
  const people: AdminPerson[] = [
    person(options.ownerEmail, 'owner'),
    ...options.members.map((email) => person(email, 'member')),
  ];

  const ordered = () =>
    [...people].sort(
      (a, b) =>
        (a.role === 'owner' ? 0 : 1) - (b.role === 'owner' ? 0 : 1) ||
        a.email.localeCompare(b.email),
    );

  const transport: AdminTransport = async (request) => {
    switch (request.op) {
      case 'listPeople':
        return { ok: true, people: ordered() };

      case 'addPerson': {
        const email = normalise(String(request.email));
        if (!EMAIL.test(email)) return { ok: false, reason: 'invalid_email' };
        if (people.some((p) => p.email === email)) return { ok: false, reason: 'already_exists' };
        if (people.filter((p) => p.role === 'member').length >= MAX_MEMBERS) {
          return { ok: false, reason: 'limit_reached' };
        }
        const added = person(email, 'member');
        people.push(added);
        return { ok: true, person: added };
      }

      case 'setEnabled': {
        const found = people.find((p) => p.id === request.id);
        if (!found) return { ok: false, reason: 'not_found' };
        if (found.role === 'owner') return { ok: false, reason: 'cannot_disable_owner' };
        found.enabled = request.enabled === true;
        return { ok: true, person: { ...found } };
      }

      default:
        return { ok: false, reason: 'invalid_request' };
    }
  };

  return {
    transport,
    canSignIn: (email) => people.find((p) => p.email === normalise(email))?.enabled ?? true,
  };
}
